import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';

import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';

const STEP = 5;

export const stepDuration = (value: number, direction: 1 | -1): number => {
  const safe = Number.isFinite(value) ? Math.max(0, value) : 0;
  if (direction === 1) {
    return Math.floor(safe / STEP) * STEP + STEP;
  }
  return Math.max(1, Math.ceil(safe / STEP) * STEP - STEP);
};

const useStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    root: { display: 'flex', flexDirection: 'column', minWidth: 0 },
    label: {
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      letterSpacing: '0.02em',
      color: t.text2,
      margin: '0 0 6px 2px',
    },
    shell: {
      display: 'flex',
      alignItems: 'stretch',
      height: 48,
      boxSizing: 'border-box',
      border: `1px solid ${t.fieldLine}`,
      borderRadius: 12,
      background: t.surface,
      overflow: 'hidden',
      transition: 'border-color 150ms ease-out, box-shadow 150ms ease-out',
      '&:hover': { borderColor: t.text2 },
      '&:focus-within': { borderColor: t.focus, boxShadow: `inset 0 0 0 1px ${t.focus}` },
    },
    step: {
      flex: '0 0 44px',
      display: 'grid',
      placeItems: 'center',
      border: 'none',
      background: 'transparent',
      color: t.text2,
      cursor: 'pointer',
      padding: 0,
      transition: 'background-color 150ms ease-out, color 150ms ease-out',
      '& svg': { fontSize: 20 },
      '&:hover': { background: t.surface3, color: t.text },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: -2 },
      '&[aria-disabled="true"]': { cursor: 'not-allowed', color: t.text3, background: 'transparent' },
    },
    value: {
      flex: '1 1 auto',
      minWidth: 0,
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'center',
      gap: 4,
      alignSelf: 'center',
    },
    input: {
      width: '3.2ch',
      minWidth: 0,
      padding: 0,
      border: 'none',
      outline: 'none',
      background: 'transparent',
      color: t.text,
      fontFamily: 'inherit',
      ...CS_FONT.title,
      fontSize: 20,
      fontWeight: CS_WEIGHT.heavy,
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
      MozAppearance: 'textfield',
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
    },
    unit: {
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.text2,
    },
    helper: {
      ...CS_FONT.caption,
      color: t.text2,
      margin: '6px 2px 0',
    },
  };
});

export interface IDurationStepperProps {
  id: string;
  value: number;
  onChange: (minutes: number) => void;
  label?: string;
  inputLabel?: string;
  helper?: string;
}

const DurationStepper: React.FC<IDurationStepperProps> = ({
  id,
  value,
  onChange,
  label = 'Duration',
  inputLabel = 'Duration in minutes',
  helper,
}) => {
  const classes = useStyles();
  const atFloor = value <= 1;
  const helperId = helper ? `${id}-helper` : undefined;
  return (
    <div className={classes.root}>
      <label htmlFor={id} className={classes.label}>{label}</label>
      <div className={classes.shell}>
        <button
          type="button"
          className={classes.step}
          aria-label={`Take off ${STEP} minutes`}
          aria-disabled={atFloor || undefined}
          onClick={() => {
            if (atFloor) return;
            onChange(stepDuration(value, -1));
          }}
        >
          <RemoveRoundedIcon aria-hidden />
        </button>
        <span className={classes.value}>
          <input
            id={id}
            className={classes.input}
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={value}
            aria-label={inputLabel}
            aria-describedby={helperId}
            onChange={e => { onChange(Math.max(0, Math.round(Number(e.target.value) || 0))); }}
          />
          <span className={classes.unit} aria-hidden="true">min</span>
        </span>
        <button
          type="button"
          className={classes.step}
          aria-label={`Add ${STEP} minutes`}
          onClick={() => { onChange(stepDuration(value, 1)); }}
        >
          <AddRoundedIcon aria-hidden />
        </button>
      </div>
      {helper ? <span id={helperId} className={classes.helper}>{helper}</span> : null}
    </div>
  );
};

export default DurationStepper;
